<script setup lang="ts">
import {onBeforeMount} from 'vue';
import {useApi} from '@/ts/composables/stateful/use-api';
import {persistentStorage} from '@/ts/instances/persistant-storage';
import {useRoute, useRouter} from 'vue-router';
import {pagesPath} from '@/ts/router/pages-path';
import {useLoadingNotification} from '@/ts/composables/pure/use-loading-notification';
import {ZITADEL} from '@/ts/utils/consts';
import {getRedirectUri} from '@/ts/composables/stateful/use-auth';
import {sessionStorage} from '@/ts/instances/session-storage';
import {useLogger} from '@/ts/composables/pure/use-logger';

const api = useApi();
const router = useRouter();
const route = useRoute();
const logger = useLogger();

const storedIntent = sessionStorage.redirectIntent;
sessionStorage.redirectIntent = null;
const redirectTo = (storedIntent?.startsWith('/') ? storedIntent : null)
  ?? (route.meta.authSuccessRedirect as string)
  ?? pagesPath.main.home;

/**
 * Zitadel returns here with a code, or — when the reader turned back at the provider, or the
 * request it was answering had already expired — with an `error` and no code. There is nothing to
 * exchange in that case, and this page holds no UI of its own, so without this it would call the
 * token endpoint with a missing code and leave them on a blank div (IPD-2844).
 */
function arrival(): {code: string | null; error: string | null} {
  // In PR the url can be ?code=asd#/public, which is why this reads the location rather than the
  // route's parsed query.
  const params = new URLSearchParams(window.location.search);
  return {code: params.get('code'), error: params.get('error')};
}

/**
 * One shape and not a union: `useLoadingNotification`'s conditional return type distributes over
 * one, so `T | null` reaches the handler as `Promise<T> | Promise<null>` and stops type-checking.
 */
interface Arrival {
  authenticated: boolean;
  registered: boolean;
}

const [submit, _] = useLoadingNotification(
  async(): Promise<Arrival> => {
    const {code, error: refusal} = arrival();
    if (!code) {
      logger.warn('continue-auth reached without an authorization code, returning to sign-up', refusal)();
      return {authenticated: false, registered: false};
    }

    const auth = await api.zitadel.getToken(
      code,
      ZITADEL.client_id,
      persistentStorage.codeVerifier!,
      getRedirectUri(),
    );
    // Must be set before asking the BE anything — the call below is authenticated.
    // eslint-disable-next-line require-atomic-updates
    persistentStorage.auth = auth;

    // A Zitadel identity is not a Balm account: signing in with Google creates the former and
    // not the latter, and every /me call would then 401 into a forced logout (IPD-2844). Ask
    // first, on a wrapper that reports 401 instead of ending the session, and route accordingly.
    //
    // Never let this probe break the sign-in it sits in the middle of. It is the only thing
    // between a successful token exchange and the app, so a BE that does not answer — the
    // endpoint not deployed yet, a blip — must degrade to the previous behaviour (carry on to
    // the app) rather than strand every user on this page. A Google user then hits the same
    // 401 as before the feature existed, which is the failure we are replacing, not a new one.
    //
    // Returned as the response object rather than a bare boolean: useLoadingNotification's
    // conditional return type distributes over `boolean` into `Promise<false> | Promise<true>`.
    // Nothing to ask where the environment has no Google provider: every identity that can
    // reach this page authenticated with a password, so it has a Balm account by construction.
    if (!ZITADEL.google_idp_id) {
      return {authenticated: true, registered: true};
    }
    try {
      const status = await api.signUp.getRegistrationStatus();
      return {authenticated: true, registered: status.registered};
    } catch (error) {
      logger.warn('registration-status unavailable, continuing as a registered user', error)();
      return {authenticated: true, registered: true};
    }
  },
  ({authenticated, registered}) => {
    // Nothing was exchanged, so there is nothing to route them into: back to where they started.
    if (!authenticated) {
      // Where they were headed outlives the failed attempt, exactly as it does when the identity
      // turns out to have no account yet: it was taken out of storage on arrival, and nothing has
      // spent it.
      sessionStorage.redirectIntent = storedIntent ?? null;
      void router.replace(pagesPath.public.signUp);
      return;
    }

    sessionStorage.completeSignUp = !registered;
    if (registered) {
      void router.replace(redirectTo);
      return;
    }
    // Registration comes first, but where they were headed survives it: put the intent back for
    // the sign-up page to spend once the account exists, instead of dropping them on home.
    sessionStorage.redirectIntent = storedIntent ?? null;
    void router.replace(pagesPath.public.signUp);
  },
);

onBeforeMount(async() => {
  await submit();
});
</script>
<template>
  <div/>
</template>
